/**
 * Practice logo handling. The uploaded file is decoded to pixels (which drops
 * any embedded metadata), downscaled, and re-encoded as PNG so transparency
 * survives. The result is small enough to keep in localStorage and never
 * leaves the device.
 */

/** Longest side of the stored logo, in pixels. Exports draw it at ≤ 50% of frame width. */
const MAX_SIDE = 600;
/** Keep the stored data URL comfortably inside localStorage limits. */
const MAX_DATA_URL_BYTES = 900_000;

function decode(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('That file could not be decoded as an image.'));
    img.src = src;
  });
}

function toCanvas(img: HTMLImageElement, maxSide: number): HTMLCanvasElement {
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.naturalWidth * scale));
  c.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return c;
}

export interface PreparedLogo {
  canvas: HTMLCanvasElement;
  /** PNG data URL, for storage. */
  dataUrl: string;
}

/** Decode + downscale an uploaded logo; shrinks further until it fits the storage budget. */
export async function prepareLogoFile(file: File): Promise<PreparedLogo> {
  const url = URL.createObjectURL(file);
  try {
    const img = await decode(url);
    for (const side of [MAX_SIDE, 400, 300, 200]) {
      const canvas = toCanvas(img, side);
      const dataUrl = canvas.toDataURL('image/png');
      if (dataUrl.length <= MAX_DATA_URL_BYTES) return { canvas, dataUrl };
    }
    throw new Error('That logo is too detailed to store. Try a simpler PNG or SVG.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Rebuild the drawable canvas from a stored data URL. */
export async function logoFromDataUrl(dataUrl: string): Promise<HTMLCanvasElement> {
  return toCanvas(await decode(dataUrl), MAX_SIDE);
}
